import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Brain, Sparkles, Clock, Target, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Attempt {
  id: string;
  is_correct: boolean;
  time_taken_seconds: number | null;
  mcqs: {
    id: string;
    stem: string;
    correct_option: string;
    difficulty?: number | null;
    tags?: string[] | null;
    topic_id?: string | null;
  };
}

interface MistakeAnalysisProps {
  attempts: Attempt[];
  topicName?: string;
}

export const MistakeAnalysis = ({ attempts, topicName }: MistakeAnalysisProps) => {
  const [insights, setInsights] = useState<string | null>(null);

  const wrong = useMemo(() => attempts.filter((a) => !a.is_correct), [attempts]);

  const stats = useMemo(() => {
    if (wrong.length === 0) return null;
    const totalTime = wrong.reduce((s, a) => s + (a.time_taken_seconds || 0), 0);
    const avgTime = Math.round(totalTime / wrong.length);
    const fastWrong = wrong.filter((a) => (a.time_taken_seconds || 0) < 20).length;
    const slowWrong = wrong.filter((a) => (a.time_taken_seconds || 0) > 90).length;
    const hardWrong = wrong.filter((a) => (a.mcqs?.difficulty || 0) >= 4).length;

    const tagCounts = new Map<string, number>();
    wrong.forEach((a) => {
      (a.mcqs?.tags || []).forEach((t) => tagCounts.set(t, (tagCounts.get(t) || 0) + 1));
    });
    const topTags = [...tagCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);

    return { avgTime, fastWrong, slowWrong, hardWrong, topTags, total: wrong.length };
  }, [wrong]);

  const analyze = useMutation({
    mutationFn: async () => {
      const payload = {
        summary: { ...stats, topic: topicName, totalQuestions: attempts.length },
        mistakes: wrong.map((a) => ({
          stem: a.mcqs?.stem,
          correct_option: a.mcqs?.correct_option,
          difficulty: a.mcqs?.difficulty,
          tags: a.mcqs?.tags,
          topic: topicName,
          time_taken_seconds: a.time_taken_seconds,
        })),
      };
      const { data, error } = await supabase.functions.invoke("ai-mistake-analyzer", { body: payload });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data.insights as string;
    },
    onSuccess: (text) => setInsights(text),
    onError: (e: any) => toast.error(e.message || "Failed to analyze"),
  });

  if (wrong.length === 0) {
    return (
      <Card className="medical-card border-medical-green/30 bg-medical-green/5">
        <CardContent className="p-6 text-center">
          <Target className="h-8 w-8 mx-auto mb-2 text-medical-green" />
          <p className="font-semibold text-foreground">Perfect score — nothing to analyze!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="medical-card border-primary/20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Brain className="h-5 w-5 text-primary" />
          Why did I get these wrong?
          <Badge variant="secondary" className="ml-auto">{wrong.length} mistake{wrong.length !== 1 ? "s" : ""}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-center">
              <Clock className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <div className="text-xl font-bold text-foreground">{stats.avgTime}s</div>
              <div className="text-xs text-muted-foreground">Avg time (wrong)</div>
            </div>
            <div className="rounded-lg border border-orange-300/50 bg-orange-50 dark:bg-orange-950/20 p-3 text-center">
              <AlertTriangle className="h-4 w-4 mx-auto mb-1 text-orange-600" />
              <div className="text-xl font-bold text-orange-600">{stats.fastWrong}</div>
              <div className="text-xs text-muted-foreground">Rushed (&lt;20s)</div>
            </div>
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-center">
              <Clock className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <div className="text-xl font-bold text-foreground">{stats.slowWrong}</div>
              <div className="text-xs text-muted-foreground">Overthought (&gt;90s)</div>
            </div>
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-center">
              <Target className="h-4 w-4 mx-auto mb-1 text-destructive" />
              <div className="text-xl font-bold text-destructive">{stats.hardWrong}</div>
              <div className="text-xs text-muted-foreground">Hard questions</div>
            </div>
          </div>
        )}

        {stats && stats.topTags.length > 0 && (
          <div>
            <div className="text-sm font-medium text-foreground mb-2">Recurring topics in mistakes:</div>
            <div className="flex flex-wrap gap-2">
              {stats.topTags.map(([tag, count]) => (
                <Badge key={tag} variant="outline" className="bg-background">
                  {tag} × {count}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {insights ? (
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">AI Coach Analysis</span>
            </div>
            <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{insights}</div>
            <Button size="sm" variant="ghost" className="mt-3" onClick={() => analyze.mutate()} disabled={analyze.isPending}>
              {analyze.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
              Regenerate
            </Button>
          </div>
        ) : (
          <Button onClick={() => analyze.mutate()} disabled={analyze.isPending} className="bg-gradient-medical w-full">
            {analyze.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing your mistakes…</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Get AI Deep-Dive Analysis</>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
